import { useState, type ReactNode } from 'react';
import { Field, Select } from '@paige/ui';
import { ActionDialog, TextInput } from './ActionDialog';
import type { ActionState, AdjustmentInput, PaidInput, Statement, StatementLine } from './pb-types';
import { day, dollars, monthWord } from './pb-words';

interface Base extends ActionState {
  account: string;
  statement: Statement;
  onClose: () => void;
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

function PaidConsequence(p: { account: string; total: number; paidOn: string }): ReactNode {
  return (
    <>
      <p>
        <span className="pb-money">{dollars(p.total)}</span> from {p.account} counts as collected on{' '}
        {day(p.paidOn)} in Revenue, and each company&rsquo;s share reaches the partner
        portal&rsquo;s payments feed.
      </p>
      <p>
        It is set once: a later correction is an adjustment on the open statement. Record only a
        payment you have seen.
      </p>
    </>
  );
}

/** Mark paid: staff record a payment they have seen in QuickBooks; Paige cannot see it, nothing is inferred. */
export function PaidDialog(
  p: Base & { today: string; onConfirm: (input: PaidInput) => void },
): ReactNode {
  const [paidOn, setPaidOn] = useState(p.today);
  const [ref, setRef] = useState('');
  const s = p.statement;
  return (
    <ActionDialog
      {...p}
      reason={false}
      title={`Mark ${s.number ?? 'the statement'} paid`}
      confirmLabel={`Mark ${dollars(s.total)} paid`}
      ready={ISO_DAY.test(paidOn)}
      onConfirm={() => p.onConfirm({ paidOn, ref: ref.trim() || null })}
      consequence={<PaidConsequence account={p.account} total={s.total} paidOn={paidOn} />}
    >
      <div className="pb-grid2">
        <TextInput label="Paid on" type="date" value={paidOn} onChange={setPaidOn} required />
        <TextInput
          label="Payment reference"
          hint="A QuickBooks payment, check or transfer number"
          value={ref}
          onChange={setRef}
          max={100}
        />
      </div>
    </ActionDialog>
  );
}

const companiesOf = (s: Statement): [string, string][] => [
  ...new Map(
    s.lines.filter((l) => l.tenantId).map((l) => [l.tenantId ?? '', l.companyName ?? '']),
  ).entries(),
];

/** Signed dollars, not 0, to the cent; NaN when it is not one. */
export const signed = (raw: string, credit: boolean): number => {
  const n = Number(raw);
  if (raw.trim() === '' || !Number.isFinite(n) || n <= 0) return NaN;
  return Math.round((credit ? -n : n) * 100) / 100;
};

interface Draft {
  company: string;
  credit: boolean;
  amount: string;
}

function WhoSelect(p: {
  statement: Statement;
  value: string;
  onChange: (v: string) => void;
}): ReactNode {
  return (
    <Field label="For">
      {({ id }) => (
        <Select id={id} value={p.value} onChange={(e) => p.onChange(e.target.value)}>
          <option value="">The whole account</option>
          {companiesOf(p.statement).map(([t, name]) => (
            <option key={t} value={t}>
              {name}
            </option>
          ))}
        </Select>
      )}
    </Field>
  );
}

/** One adjustment's fields: whose, a credit or a charge, how much. */
function AdjustmentFields(p: {
  statement: Statement;
  d: Draft;
  onChange: (d: Draft) => void;
}): ReactNode {
  return (
    <div className="pb-grid2">
      <WhoSelect
        statement={p.statement}
        value={p.d.company}
        onChange={(company) => p.onChange({ ...p.d, company })}
      />
      <Field label="Kind">
        {({ id }) => (
          <Select
            id={id}
            value={p.d.credit ? 'credit' : 'charge'}
            onChange={(e) => p.onChange({ ...p.d, credit: e.target.value === 'credit' })}
          >
            <option value="credit">Credit (lowers what is owed)</option>
            <option value="charge">Charge (raises it)</option>
          </Select>
        )}
      </Field>
      <TextInput
        label="Amount ($)"
        type="number"
        step="0.01"
        inputMode="decimal"
        value={p.d.amount}
        onChange={(amount) => p.onChange({ ...p.d, amount })}
        required
      />
    </div>
  );
}

function AdjustConsequence(p: {
  account: string;
  value: number;
  credit: boolean;
  openMonth: string | null;
  corrects: StatementLine | null;
}): ReactNode {
  const where = p.openMonth
    ? `the open ${monthWord(p.openMonth)} statement`
    : 'this month’s open statement';
  return (
    <>
      <p>
        {Number.isNaN(p.value) ? (
          'The adjustment'
        ) : (
          <span className="pb-money">{dollars(p.value)}</span>
        )}{' '}
        goes on {where}, issued on the 1st:{' '}
        {p.credit
          ? `a credit lowers what ${p.account} owes`
          : `a charge raises what ${p.account} owes`}
        .
      </p>
      {p.corrects ? (
        <p>
          It names line {p.corrects.id} ({p.corrects.description}, {dollars(p.corrects.amount)}) as
          the line it corrects; the issued statement itself never changes.
        </p>
      ) : null}
    </>
  );
}

/** Add adjustment: a signed line on the account's open statement, naming the issued line it corrects when it does. */
type AdjustProps = Base & {
  corrects: StatementLine | null;
  openMonth: string | null;
  onConfirm: (input: AdjustmentInput) => void;
};

export function AdjustDialog(p: AdjustProps): ReactNode {
  const [d, setD] = useState<Draft>({
    company: p.corrects?.tenantId ?? '',
    credit: true,
    amount: '',
  });
  const value = signed(d.amount, d.credit);
  const input = (reason: string): AdjustmentInput => ({
    ...{ tenantId: d.company || null, amount: value },
    ...{ reason, corrects: p.corrects?.id ?? null },
  });
  return (
    <ActionDialog
      {...p}
      title={p.corrects ? `Correct line ${p.corrects.id}` : `Add an adjustment for ${p.account}`}
      confirmLabel={Number.isNaN(value) ? 'Add adjustment' : `Add ${dollars(value)}`}
      ready={!Number.isNaN(value)}
      onConfirm={(reason) => p.onConfirm(input(reason))}
      consequence={<AdjustConsequence {...p} value={value} credit={d.credit} />}
    >
      <AdjustmentFields statement={p.statement} d={d} onChange={setD} />
    </ActionDialog>
  );
}
